import { INVENTORY_FILTERS_KEYS } from '@silverfort-nodelibs/common/utils/consts';
import { Dropdown, Search } from '@silverfort/sf-components';
import { FC, ReactElement } from 'react';
import { INVENTORY_AD_TABS } from './InventoryActiveDirectoryBuilders';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { RISK_LEVELS_OPTIONS, RISK_INDICATORS_OPTIONS } from './filterOptions';
import { useGet } from '../../api/queries/useGet';

interface InventoryFiltersProps {
    selectedTab: number;
    domainsList: string[];
    selectedFilters: { [key: string]: string[] | number[] | boolean[] };
    setSelectedFilters: (selectedFilters: { [key: string]: string[] | number[] | boolean[] }) => void;
    searchValue: string;
    setSearchValue: (text: string) => void;
}

const InventoryFilters: FC<InventoryFiltersProps> = ({
    selectedTab,
    domainsList,
    selectedFilters,
    setSelectedFilters,
    searchValue,
    setSearchValue
}: InventoryFiltersProps) => {
    const filters: ReactElement[] = [];
    const { pricingModulesState } = usePricingModulesState();

    const filterByRisk =
        selectedTab === INVENTORY_AD_TABS[0].value &&
        (pricingModulesState?.itdr?.onPremEnabled || pricingModulesState?.ispm?.onPremEnabled);

    const { data: hiddenRiskIndicators = [] } = useGet<string[]>({
        url: '/inventory/hiddenRiskIndicators',
        enabled: filterByRisk
    });
    const filterByDisabled = selectedTab === INVENTORY_AD_TABS[0].value;

    const onRemoveFilter = (filterKey: string) => {
        const newSelectedFilters = { ...selectedFilters };
        delete newSelectedFilters[filterKey];
        setSelectedFilters(newSelectedFilters);
    };

    const onChangeFilter = (filterKey: string, value?: string[] | number[] | boolean[]) => {
        if (!value?.length) {
            return onRemoveFilter(filterKey);
        }
        setSelectedFilters({ ...selectedFilters, [filterKey]: value });
    };

    if (domainsList.length) {
        const filterKey = INVENTORY_FILTERS_KEYS.DOMAINS;
        const domainFilter = (
            <Dropdown
                multiple
                title="Domains"
                items={domainsList.map((domain) => ({ title: domain, value: domain }))}
                key={filterKey}
                variant="filter"
                searchable={false}
                selectedItems={(selectedFilters[filterKey] as string[]) || []}
                removable={!!selectedFilters[filterKey]?.length}
                onRemove={() => onRemoveFilter(filterKey)}
                onChange={(val) => onChangeFilter(filterKey, val as string[])}
            />
        );

        filters.push(domainFilter);
    }

    if (filterByRisk) {
        const filterKey = INVENTORY_FILTERS_KEYS.RISK;
        const riskFilter = (
            <Dropdown
                multiple
                items={RISK_LEVELS_OPTIONS}
                title="Risk"
                key={filterKey}
                variant="filter"
                searchable={false}
                selectedItems={(selectedFilters[filterKey] as number[]) || []}
                removable={!!selectedFilters[filterKey]?.length}
                onRemove={() => onRemoveFilter(filterKey)}
                onChange={(val) => onChangeFilter(filterKey, val as number[])}
            />
        );

        const riskIndicatorFilterKey = INVENTORY_FILTERS_KEYS.RISK_INDICATORS;
        const riskIndicator = (
            <Dropdown
                multiple
                title="Risk Indicator"
                variant="filter"
                searchable
                selected
                disableSelectAll
                items={RISK_INDICATORS_OPTIONS.filter(({ value }) => !hiddenRiskIndicators.includes(value))}
                key={riskIndicatorFilterKey}
                selectedItems={(selectedFilters[riskIndicatorFilterKey] as string[]) || []}
                removable={!!selectedFilters[riskIndicatorFilterKey]?.length}
                onRemove={() => onRemoveFilter(riskIndicatorFilterKey)}
                onChange={(val) => onChangeFilter(riskIndicatorFilterKey, val as string[])}
            />
        );

        filters.push(riskFilter, riskIndicator);
    }

    if (filterByDisabled) {
        const filterKey = INVENTORY_FILTERS_KEYS.DISABLED;
        const disabledFilter = (
            <Dropdown
                multiple
                items={[
                    { title: 'Yes', value: true },
                    { title: 'No', value: false }
                ]}
                title="Disabled"
                key={filterKey}
                variant="filter"
                searchable={false}
                selectedItems={(selectedFilters[filterKey] as boolean[]) || []}
                removable={!!selectedFilters[filterKey]?.length}
                onRemove={() => onRemoveFilter(filterKey)}
                onChange={(val) => onChangeFilter(filterKey, val as boolean[])}
            />
        );

        filters.push(disabledFilter);
    }

    return (
        <div className="h-8 w-full flex justify-between">
            <div className="flex gap-2">{filters}</div>
            <div className="flex gap-2">
                <Search
                    border
                    text={searchValue}
                    onChange={(val) => setSearchValue(val)}
                    placeholder={
                        'Search ' +
                        (INVENTORY_AD_TABS.find((tab) => tab.value === selectedTab)?.name?.toLowerCase() || '')
                    }
                />
            </div>
        </div>
    );
};

export default InventoryFilters;
